import { useCallback, useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { UploadCloud, File as FileIcon, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

interface FileDropzoneProps {
  onUpload: (files: File[]) => Promise<void>;
  accept?: string;
  maxSize?: number;
  label?: string;
  multiple?: boolean;
}

export function FileDropzone({
  onUpload,
  accept = ".pdf",
  maxSize = 10 * 1024 * 1024,
  label = "Drop PDF here",
  multiple = false,
}: FileDropzoneProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const allowedExtensions = accept.split(",").map((s) => s.trim().toLowerCase());
  const acceptedFileLabel = allowedExtensions.some((ext) => [".xlsx", ".xls", ".csv"].includes(ext))
    ? "Excel spreadsheets"
    : multiple
      ? "Select multiple files"
      : "PDF files";

  const validateFile = useCallback(
    (f: File): string | null => {
      const ext = "." + f.name.split(".").pop()?.toLowerCase();
      const isAllowed =
        allowedExtensions.includes(ext) ||
        allowedExtensions.some((a) => {
          if (a === ".pdf") return f.type === "application/pdf" || ext === ".pdf";
          if (a === ".png") return f.type === "image/png" || ext === ".png";
          if (a === ".jpg" || a === ".jpeg") return f.type.startsWith("image/jpeg") || ext === ".jpg" || ext === ".jpeg";
          return false;
        });
      if (!isAllowed) return `"${f.name}" is not an accepted file type.`;
      if (f.size > maxSize) return `"${f.name}" exceeds ${Math.round(maxSize / 1024 / 1024)}MB limit.`;
      return null;
    },
    [allowedExtensions, maxSize],
  );

  const addFiles = useCallback(
    (newFiles: FileList) => {
      setError(null);
      const toAdd: File[] = [];
      for (let i = 0; i < newFiles.length; i++) {
        const err = validateFile(newFiles[i]);
        if (err) {
          setError(err);
          if (!multiple) return;
          continue;
        }
        toAdd.push(newFiles[i]);
      }
      if (toAdd.length === 0) return;
      if (multiple) {
        setFiles((prev) => {
          const existing = new Set(prev.map((f) => f.name + f.size));
          const unique = toAdd.filter((f) => !existing.has(f.name + f.size));
          return [...prev, ...unique];
        });
      } else {
        setFiles([toAdd[0]]);
      }
    },
    [multiple, validateFile],
  );

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
      }
      if (inputRef.current) inputRef.current.value = "";
    },
    [addFiles],
  );

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setIsUploading(true);
    try {
      await onUpload(files);
      setFiles([]);
    } finally {
      setIsUploading(false);
    }
  };

  if (files.length > 0) {
    return (
      <div className="w-full">
        <div className="border border-slate-200 rounded-2xl p-6 bg-white shadow-sm flex flex-col gap-3">
          <div className="max-h-60 overflow-y-auto space-y-2">
            {files.map((f, i) => (
              <div key={f.name + f.size} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileIcon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                  <p className="text-xs text-slate-500">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button
                  onClick={() => removeFile(i)}
                  disabled={isUploading}
                  className="p-1.5 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {multiple && !isUploading && (
            <button
              onClick={() => inputRef.current?.click()}
              className="text-xs text-primary hover:underline self-start"
            >
              + Add more files
            </button>
          )}

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleFileChange}
            className="hidden"
          />

          <Button className="w-full" onClick={handleUpload} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading {files.length} file{files.length > 1 ? "s" : ""}...
              </>
            ) : (
              <>Upload {files.length} file{files.length > 1 ? "s" : ""}</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors duration-200",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
        />
        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
          <UploadCloud className={cn("w-8 h-8", isDragOver ? "text-primary" : "text-slate-400")} />
        </div>
        <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
        <p className="text-xs text-slate-500 text-center">
          Click to browse or drag and drop
          <br />
          {acceptedFileLabel} up to {Math.round(maxSize / 1024 / 1024)}MB
        </p>
        <p className="text-[11px] text-slate-400 text-center mt-3 max-w-xs leading-relaxed">
          Tip: Outlook attachments can't be dragged directly into the browser. Drag them to your desktop first, or use Save As, then upload from there.
        </p>
        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
      </div>
    </div>
  );
}
